import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Tabs,
  Tab
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ExtendedReceivedItem, JobWorkReceipt } from './types';
import { useSnackbar } from 'notistack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import jobWorksApi from '../../api/jobWorks';

interface ReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  jobWorkId: number;
  designNo: string;
  designDetails: Array<{
    garmentPart: string;
    size: string;
    pieces: number;
    rate: number;
  }>;
  editingReceiptId?: number;
  onSave: (receipt: any) => Promise<void>;
  processMaterial: boolean;
  materials?: Array<{
    materialId: number;
    materialCode: string;
    materialType: string;
    quantity: number;
    uom: string;
    rate: number;
  }>;
}

const ReceiptDialog: React.FC<ReceiptDialogProps> = ({
  open,
  onClose,
  jobWorkId,
  designNo,
  designDetails,
  editingReceiptId,
  onSave,
  processMaterial,
  materials = []
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [receiveDate, setReceiveDate] = useState<Date | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<number>(0);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [invoiceImageUrl, setInvoiceImageUrl] = useState('');
  const [receivedItems, setReceivedItems] = useState<ExtendedReceivedItem[]>([]);
  const [invoiceFileType, setInvoiceFileType] = useState<'image' | 'pdf' | null>(null);
  const [useCalculatedAmount, setUseCalculatedAmount] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Pending receipt maps
  const [pendingByMaterial, setPendingByMaterial] = useState<Record<number, number>>({});
  const [pendingByDesign, setPendingByDesign] = useState<Record<string, number>>({});

  // Calculate total amount based on received items
  const calculatedAmount = useMemo(() => {
    return receivedItems.reduce((total, item) => {
      // Determine if it's a material or design item
      const isMaterial = !!item.materialId;
      
      // For material items, use materialQuantity; for design items, use pieceCount
      const quantity = isMaterial ? (item.materialQuantity || 0) : (item.pieceCount || 0);
      const itemTotal = quantity * (item.processingRate || 0);
      
      return total + itemTotal;
    }, 0);
  }, [receivedItems]);

  // Update invoice amount when the calculated amount changes
  useEffect(() => {
    if (useCalculatedAmount) {
      setInvoiceAmount(calculatedAmount);
    }
  }, [calculatedAmount, useCalculatedAmount]);

  // Handle manual invoice amount change
  const handleInvoiceAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? Number(e.target.value) : 0;
    setInvoiceAmount(value);
    setUseCalculatedAmount(false);
  };

  const handleItemChange = (index: number, field: keyof ExtendedReceivedItem, value: any) => {
    if (index < 0 || index >= receivedItems.length) return;
    
    const updatedItems = [...receivedItems];
    
    // Update the specific field without any automatic synchronization
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    setReceivedItems(updatedItems);
    
    // If changing a quantity field (pieceCount or materialQuantity), check for potential validation issues
    if (field === 'pieceCount' || field === 'materialQuantity') {
      // We'll do a light validation here to give immediate feedback
      // Full validation is still done when submitting
      validateQuantitiesForItem(updatedItems[index], updatedItems);
    }
  };

  // Lightweight validation for a single item to provide immediate feedback
  const validateQuantitiesForItem = async (changedItem: ExtendedReceivedItem, allItems: ExtendedReceivedItem[]) => {
    // Only run validation if we have necessary data
    if (!changedItem.garmentPart || !changedItem.size) return;
    
    try {
      // First, clear any existing validation errors related to this item
      const filteredErrors = validationErrors.filter(error => {
        // If material item
        if (changedItem.materialId) {
          return !error.includes(`Material #${changedItem.materialId}`);
        } 
        // If design item
        else {
          return !error.includes(`Design ${changedItem.garmentPart}/${changedItem.size}`);
        }
      });
      
      // Get the job work data to do validation
      const response = await jobWorksApi.getJobWorkById(jobWorkId);
      const jobWork = response.data;
      
      let newErrors: string[] = [];
      
      // For design items
      if (!changedItem.materialId && jobWork.designDetails && jobWork.designDetails.length > 0) {
        const key = `${changedItem.garmentPart}:${changedItem.size}`;
        
        // Find matching design detail to get the allowed quantity
        const designDetail = jobWork.designDetails.find((detail: any) => 
          detail.garmentPart === changedItem.garmentPart && detail.size === changedItem.size
        );
        
        if (designDetail) {
          const issuedPcs = designDetail.pieces || 0;
          
          // Calculate already received pieces from previous receipts (excluding current editing receipt)
          const previousReceipts = jobWork.receivedItems.filter((item: any) => {
            if (editingReceiptId) {
              const currentReceiptItem = jobWork.receivedItems.find((r: any) => r.id === editingReceiptId);
              if (currentReceiptItem && item.createdAt === currentReceiptItem.createdAt) {
                return false; // exclude items from current receipt
              }
            }
            return true;
          });
          
          // Calculate total received quantity for this garment part/size combo
          let alreadyReceived = 0;
          previousReceipts.forEach((item: any) => {
            if (item.garmentPart === changedItem.garmentPart && 
                item.size === changedItem.size && 
                !item.materialId) {
              // Sum all quantities: pieceCount + defectQuantity + shortageQuantity
              alreadyReceived += (item.pieceCount || 0) + (item.defectQuantity || 0) + (item.shortageQuantity || 0);
            }
          });
          
          // Calculate total of current item including all relevant quantities
          const currentTotal = (changedItem.pieceCount || 0) + (changedItem.defectQuantity || 0) + (changedItem.shortageQuantity || 0);
          
          // Check if the current value would exceed the limit
          if (alreadyReceived + currentTotal > issuedPcs) {
            newErrors.push(`Design ${changedItem.garmentPart}/${changedItem.size}: Total received pieces (${alreadyReceived + currentTotal}) exceeds issued pieces (${issuedPcs})`);
          }
        }
      }
      
      // For material items
      if (changedItem.materialId && jobWork.materials && jobWork.materials.length > 0) {
        const material = jobWork.materials.find((m: any) => m.materialId === changedItem.materialId);
        
        if (material) {
          const issuedQty = material.quantity || 0;
          
          // Calculate already received material quantities
          const previousReceipts = jobWork.receivedItems.filter((item: any) => {
            if (editingReceiptId) {
              const currentReceiptItem = jobWork.receivedItems.find((r: any) => r.id === editingReceiptId);
              if (currentReceiptItem && item.createdAt === currentReceiptItem.createdAt) {
                return false;
              }
            }
            return true;
          });
          
          let alreadyReceived = 0;
          previousReceipts.forEach((item: any) => {
            if (item.materialId === changedItem.materialId) {
              // Sum all related quantities: materialQuantity + wastageQuantity
              alreadyReceived += (item.materialQuantity || 0) + (item.wastageQuantity || 0);
            }
          });
          
          // Calculate total of current item including wastage
          const currentTotal = (changedItem.materialQuantity || 0) + (changedItem.wastageQuantity || 0);
          
          if (alreadyReceived + currentTotal > issuedQty) {
            newErrors.push(`Material #${changedItem.materialId}: Total received quantity (${alreadyReceived + currentTotal}) exceeds issued quantity (${issuedQty})`);
          }
        }
      }
      
      // Update validation errors
      if (newErrors.length > 0 || filteredErrors.length !== validationErrors.length) {
        setValidationErrors([...filteredErrors, ...newErrors]);
        setShowValidationErrors(newErrors.length > 0 || filteredErrors.length > 0);
      }
      
    } catch (error) {
      console.error('Error validating item quantities:', error);
    }
  };

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && processMaterial) {
      return;
    }
    setActiveTab(newValue);
  };

  // Update active tab when dialog opens or processMaterial changes
  useEffect(() => {
    if (open) {
      setActiveTab(processMaterial ? 0 : 1);
    }
  }, [open, processMaterial]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      // Set default receive date to today
      setReceiveDate(new Date());
      setInvoiceDate(new Date());
      
      // Initialize with empty state
      const initialItems: ExtendedReceivedItem[] = [];
      
      // Add design program items if they exist
      if (!processMaterial && designDetails && designDetails.length > 0) {
        designDetails.forEach(design => {
          initialItems.push({
            jobWorkId,
            designNo,
            garmentPart: design.garmentPart,
            size: design.size,
            quantity: design.pieces,
            uom: 'PCS',
            processingRate: design.rate,
            materialQuantity: 0,
            materialUom: 'PCS',
            pieceCount: 0,
            defectQuantity: 0,
            shortageQuantity: 0,
            wastageQuantity: 0,
            remarks: ''
          });
        });
      }
      
      // Add material items if they exist
      if (processMaterial && materials && materials.length > 0) {
        materials.forEach(material => {
          initialItems.push({
            jobWorkId,
            designNo,
            garmentPart: '',
            size: '',
            quantity: material.quantity,
            uom: material.uom,
            processingRate: material.rate,
            materialId: material.materialId,
            materialQuantity: 0,
            materialUom: material.uom,
            pieceCount: 0,
            defectQuantity: 0,
            shortageQuantity: 0,
            wastageQuantity: 0,
            remarks: ''
          });
        });
      }
      
      setReceivedItems(initialItems);
      
      // Reset other fields
      setInvoiceNumber('');
      setInvoiceAmount(0);
      setTaxAmount(0);
      setInvoiceImageUrl('');
      setInvoiceFileType(null);
      setUseCalculatedAmount(true);
      setValidationErrors([]);
      setShowValidationErrors(false);
      
      // If editing, fetch the receipt data
      if (editingReceiptId) {
        fetchReceiptData();
      }
      computePendingQuantities();
    }
  }, [open, jobWorkId, designNo, designDetails, editingReceiptId, materials, processMaterial]);

  const computePendingQuantities = async () => {
    try {
      const response = await jobWorksApi.getJobWorkById(jobWorkId);
      const jobWork = response.data;

      // Identify current receipt timestamp to exclude if editing
      let excludeCreatedAt: string | undefined;
      if (editingReceiptId) {
        const currentReceiptItem = jobWork.receivedItems.find((r: any) => r.id === editingReceiptId);
        excludeCreatedAt = currentReceiptItem?.createdAt;
      }

      // Materials: issued and received so far
      const issuedM = new Map<number, number>();
      (jobWork.materials || []).forEach((m: any) => {
        issuedM.set(m.materialId, (issuedM.get(m.materialId) || 0) + (m.quantity || 0));
      });
      const receivedM = new Map<number, number>();
      (jobWork.receivedItems || []).forEach((r: any) => {
        if (excludeCreatedAt && r.createdAt === excludeCreatedAt) return;
        if (r.materialId) {
          const qty = (r.materialQuantity || 0) + (r.wastageQuantity || 0);
          receivedM.set(r.materialId, (receivedM.get(r.materialId) || 0) + qty);
        }
      });
      const pendingM: Record<number, number> = {};
      issuedM.forEach((qty, id) => {
        pendingM[id] = Math.max(0, qty - (receivedM.get(id) || 0));
      });
      setPendingByMaterial(pendingM);

      // Design details: issued and received so far
      const issuedD = new Map<string, number>();
      (jobWork.designDetails || []).forEach((d: any) => {
        const key = `${d.garmentPart || ''}|${d.size || ''}`;
        issuedD.set(key, (issuedD.get(key) || 0) + (d.pieces || 0));
      });
      const receivedD = new Map<string, number>();
      (jobWork.receivedItems || []).forEach((r: any) => {
        if (excludeCreatedAt && r.createdAt === excludeCreatedAt) return;
        if (!r.materialId) {
          const key = `${r.garmentPart || ''}|${r.size || ''}`;
          const qty = (r.pieceCount || 0) + (r.defectQuantity || 0) + (r.shortageQuantity || 0);
          receivedD.set(key, (receivedD.get(key) || 0) + qty);
        }
      });
      const pendingD: Record<string, number> = {};
      issuedD.forEach((qty, key) => {
        pendingD[key] = Math.max(0, qty - (receivedD.get(key) || 0));
      });
      setPendingByDesign(pendingD);
    } catch (error) {
      console.error('Failed to compute pending quantities:', error);
    }
  };

  // Fetch receipt data for editing
  const fetchReceiptData = async () => {
    if (!editingReceiptId) return;

    try {
      setLoading(true);

      const response = await jobWorksApi.getJobWorkById(jobWorkId);
      const jw = response.data;
      const allItems: any[] = jw.receivedItems || [];

      // Find the target receipt item
      const targetItem = allItems.find((r: any) => r.id === editingReceiptId);
      if (!targetItem) {
        enqueueSnackbar('Receipt not found', { variant: 'error' });
        return;
      }

      // Get all items from the same receipt batch (same createdAt timestamp)
      const batchItems = allItems.filter((r: any) => r.createdAt === targetItem.createdAt);

      // Set receive date from the receipt
      if (targetItem.createdAt) {
        setReceiveDate(new Date(targetItem.createdAt));
      }

      // Merge batch items into the initialized form rows
      setReceivedItems(prev => {
        const updated = prev.map(item => ({ ...item }));
        const matched = new Set<number>();

        for (const batchItem of batchItems) {
          let matchIdx = -1;

          // Strategy 1: match by materialId
          if (batchItem.materialId) {
            matchIdx = updated.findIndex((item, idx) => !matched.has(idx) && item.materialId === batchItem.materialId);
          }

          // Strategy 2: match by garmentPart + size (design-based items)
          if (matchIdx < 0 && (batchItem.garmentPart || batchItem.size)) {
            matchIdx = updated.findIndex((item, idx) => !matched.has(idx) &&
              item.garmentPart === batchItem.garmentPart && item.size === batchItem.size
            );
          }

          // Strategy 3: processMaterial receipts with no materialId and empty part/size
          // — match any unmatched material item in the form
          if (matchIdx < 0) {
            matchIdx = updated.findIndex((item, idx) => !matched.has(idx) && !!item.materialId);
          }

          if (matchIdx >= 0) {
            matched.add(matchIdx);
            updated[matchIdx] = {
              ...updated[matchIdx],
              id: batchItem.id,
              pieceCount: batchItem.pieceCount || 0,
              defectQuantity: batchItem.defectQuantity || 0,
              shortageQuantity: batchItem.shortageQuantity || 0,
              wastageQuantity: batchItem.wastageQuantity || 0,
              materialQuantity: batchItem.materialQuantity || 0,
              processingRate: batchItem.processingRate ?? updated[matchIdx].processingRate ?? 0,
              remarks: batchItem.remarks || ''
            };
          }
        }
        return updated;
      });
    } catch (error) {
      console.error('Error fetching receipt data:', error);
      enqueueSnackbar('Failed to load receipt data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Validate received quantities
  const validateReceivedQuantities = async (): Promise<string[]> => {
    const errors: string[] = [];
    
    try {
      // Get the job work data to do validation
      const response = await jobWorksApi.getJobWorkById(jobWorkId);
      const jobWork = response.data;
      
      // For each received item, validate against the issued quantities
      for (const item of receivedItems) {
        // Skip items with no quantities
        if ((!item.materialId && item.pieceCount === 0 && item.defectQuantity === 0 && item.shortageQuantity === 0) ||
            (item.materialId && item.materialQuantity === 0 && item.wastageQuantity === 0)) {
          continue;
        }
        
        // For design items
        if (!item.materialId && jobWork.designDetails && jobWork.designDetails.length > 0) {
          const designDetail = jobWork.designDetails.find((detail: any) => 
            detail.garmentPart === item.garmentPart && detail.size === item.size
          );
          
          if (designDetail) {
            const issuedPcs = designDetail.pieces || 0;
            
            // Calculate already received pieces from previous receipts (excluding current editing receipt)
            const previousReceipts = jobWork.receivedItems.filter((r: any) => {
              if (editingReceiptId) {
                const currentReceiptItem = jobWork.receivedItems.find((rec: any) => rec.id === editingReceiptId);
                if (currentReceiptItem && r.createdAt === currentReceiptItem.createdAt) {
                  return false; // exclude items from current receipt
                }
              }
              return true;
            });
            
            // Calculate total received quantity for this garment part/size combo
            let alreadyReceived = 0;
            previousReceipts.forEach((r: any) => {
              if (r.garmentPart === item.garmentPart && 
                  r.size === item.size && 
                  !r.materialId) {
                // Sum all quantities: pieceCount + defectQuantity + shortageQuantity
                alreadyReceived += (r.pieceCount || 0) + (r.defectQuantity || 0) + (r.shortageQuantity || 0);
              }
            });
            
            // Calculate total of current item including all relevant quantities
            const currentTotal = (item.pieceCount || 0) + (item.defectQuantity || 0) + (item.shortageQuantity || 0);
            
            // Check if the current value would exceed the limit
            if (alreadyReceived + currentTotal > issuedPcs) {
              errors.push(`Design ${item.garmentPart}/${item.size}: Total received pieces (${alreadyReceived + currentTotal}) exceeds issued pieces (${issuedPcs})`);
            }
          }
        }
        
        // For material items
        if (item.materialId && jobWork.materials && jobWork.materials.length > 0) {
          const material = jobWork.materials.find((m: any) => m.materialId === item.materialId);
          
          if (material) {
            const issuedQty = material.quantity || 0;
            
            // Calculate already received material quantities
            const previousReceipts = jobWork.receivedItems.filter((r: any) => {
              if (editingReceiptId) {
                const currentReceiptItem = jobWork.receivedItems.find((rec: any) => rec.id === editingReceiptId);
                if (currentReceiptItem && r.createdAt === currentReceiptItem.createdAt) {
                  return false;
                }
              }
              return true;
            });
            
            let alreadyReceived = 0;
            previousReceipts.forEach((r: any) => {
              if (r.materialId === item.materialId) {
                // Sum all related quantities: materialQuantity + wastageQuantity
                alreadyReceived += (r.materialQuantity || 0) + (r.wastageQuantity || 0);
              }
            });
            
            // Calculate total of current item including wastage
            const currentTotal = (item.materialQuantity || 0) + (item.wastageQuantity || 0);
            
            if (alreadyReceived + currentTotal > issuedQty) {
              errors.push(`Material #${item.materialId}: Total received quantity (${alreadyReceived + currentTotal}) exceeds issued quantity (${issuedQty})`);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error validating received quantities:', error);
      errors.push('Error validating quantities. Please try again.');
    }
    
    return errors;
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!receiveDate) {
        enqueueSnackbar('Receive date is required', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Validate received quantities
      const validationErrors = await validateReceivedQuantities();
      if (validationErrors.length > 0) {
        setValidationErrors(validationErrors);
        setShowValidationErrors(true);
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Filter out items with no quantities
      const filteredItems = receivedItems.filter(item => {
        if (item.materialId) {
          // For material items, check if materialQuantity or wastageQuantity is greater than 0
          return (item.materialQuantity > 0 || item.wastageQuantity > 0);
        } else {
          // For design items, check if pieceCount, defectQuantity, or shortageQuantity is greater than 0
          return (item.pieceCount > 0 || item.defectQuantity > 0 || item.shortageQuantity > 0);
        }
      });
      
      if (filteredItems.length === 0) {
        enqueueSnackbar('At least one item must have a quantity greater than 0', { variant: 'error' });
        setLoading(false);
        return;
      }
      
      // Prepare the receipt data
      const receiptData = {
        jobWorkId,
        receiveDate: format(receiveDate, 'yyyy-MM-dd'),
        receivedItems: filteredItems.map(item => ({
          id: item.id,
          jobWorkId: item.jobWorkId,
          designNo: item.designNo,
          garmentPart: item.garmentPart,
          size: item.size,
          quantity: item.quantity,
          uom: item.uom,
          processingRate: item.processingRate,
          materialId: item.materialId,
          materialQuantity: item.materialQuantity,
          materialUom: item.materialUom,
          pieceCount: item.pieceCount,
          defectQuantity: item.defectQuantity,
          shortageQuantity: item.shortageQuantity,
          wastageQuantity: item.wastageQuantity,
          remarks: item.remarks
        }))
      };
      
      // Save the receipt
      await onSave(receiptData);
      
      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Error saving receipt:', error);
      enqueueSnackbar('Failed to save receipt', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle invoice upload
  const handleInvoiceUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingInvoice(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload the file
      const response = await jobWorksApi.uploadInvoiceImage(formData);
      
      // Set the invoice image URL
      setInvoiceImageUrl(response.data.url);
      setInvoiceFileType(file.type.includes('pdf') ? 'pdf' : 'image');
      
      enqueueSnackbar('Invoice uploaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error uploading invoice:', error);
      enqueueSnackbar('Failed to upload invoice', { variant: 'error' });
    } finally {
      setUploadingInvoice(false);
    }
  };

  // Handle remove invoice
  const handleRemoveInvoice = () => {
    setInvoiceImageUrl('');
    setInvoiceFileType(null);
  };

  // Render invoice preview
  const renderInvoicePreview = () => {
    if (!invoiceImageUrl) return null;
    
    if (invoiceFileType === 'pdf') {
      return (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />
          <Typography variant="body2">PDF Invoice</Typography>
          <IconButton 
            size="small" 
            onClick={handleRemoveInvoice}
            sx={{ ml: 'auto' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    } else {
      return (
        <Box sx={{ mt: 2, position: 'relative' }}>
          <Box
            component="img"
            src={invoiceImageUrl}
            alt="Invoice"
            sx={{ 
              maxWidth: '100%', 
              maxHeight: 200, 
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          />
          <IconButton 
            size="small" 
            onClick={handleRemoveInvoice}
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8, 
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }
  };

  // Handle reset to calculated amount
  const handleResetToCalculated = () => {
    setInvoiceAmount(calculatedAmount);
    setUseCalculatedAmount(true);
  };

  // Validation errors component
  const ValidationErrors = () => {
    if (!showValidationErrors || validationErrors.length === 0) return null;
    
    return (
      <Collapse in={showValidationErrors}>
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowValidationErrors(false)}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <List dense>
            {validationErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText 
                  primary={error} 
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      </Collapse>
    );
  };

  // Check if an item has an error
  const hasItemError = (item: ExtendedReceivedItem, field: keyof ExtendedReceivedItem): boolean => {
    return validationErrors.some(error => {
      if (item.materialId) {
        return error.includes(`Material #${item.materialId}`);
      } else {
        return error.includes(`Design ${item.garmentPart}/${item.size}`);
      }
    });
  };

  // Get error text for an item
  const getItemErrorText = (item: ExtendedReceivedItem, field: keyof ExtendedReceivedItem): string => {
    const error = validationErrors.find(e => {
      if (item.materialId) {
        return e.includes(`Material #${item.materialId}`);
      } else {
        return e.includes(`Design ${item.garmentPart}/${item.size}`);
      }
    });
    
    return error || '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {editingReceiptId ? 'Edit Receipt' : 'Add Receipt'}
      </DialogTitle>
      
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Receive Date"
                value={receiveDate}
                onChange={(date) => setReceiveDate(date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: 120,
                      textTransform: 'none',
                      fontWeight: 500
                    }
                  }}
                >
                  <Tab 
                    label="Materials" 
                    sx={{ 
                      color: processMaterial ? 'primary.main' : 'text.secondary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 600
                      }
                    }}
                  />
                  <Tab 
                    label="Design Details" 
                    disabled={processMaterial}
                    sx={{ 
                      color: !processMaterial ? 'primary.main' : 'text.disabled',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 600
                      }
                    }}
                  />
                </Tabs>
              </Box>
              
              {activeTab === 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Material</TableCell>
                        <TableCell align="right">Pending Receipt</TableCell>
                        <TableCell align="right">Receive Qty</TableCell>
                        <TableCell align="right">Wastage Qty</TableCell>
                        {processMaterial && (
                          <>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </>
                        )}
                        <TableCell>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {materials && materials.length > 0 ? (
                        materials.map((material, materialIndex) => {
                          let receivedItem = receivedItems.find(item => item.materialId === material.materialId);
                          
                          // If no received item exists for this material, create one
                          if (!receivedItem) {
                            receivedItem = {
                              jobWorkId,
                              designNo,
                              garmentPart: '',
                              size: '',
                              quantity: material.quantity,
                              uom: material.uom,
                              processingRate: material.rate,
                              materialId: material.materialId,
                              materialQuantity: 0,
                              materialUom: material.uom,
                              pieceCount: 0,
                              defectQuantity: 0,
                              shortageQuantity: 0,
                              wastageQuantity: 0,
                              remarks: ''
                            };
                            // Add the new item to receivedItems
                            setReceivedItems([...receivedItems, receivedItem]);
                          }
                          
                          const receivedItemIndex = receivedItems.findIndex(item => item.materialId === material.materialId);
                          
                          return (
                            <TableRow key={materialIndex}>
                              <TableCell>
                                {material.materialCode} - {material.materialType}
                              </TableCell>
                              <TableCell align="right">
                                {pendingByMaterial[material.materialId] ?? 0} {material.uom}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.materialQuantity || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'materialQuantity',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  error={hasItemError(receivedItem, 'materialQuantity')}
                                  helperText={getItemErrorText(receivedItem, 'materialQuantity')}
                                  InputProps={{
                                    inputProps: { min: 0 }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.wastageQuantity || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'wastageQuantity',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  error={hasItemError(receivedItem, 'wastageQuantity')}
                                  helperText={getItemErrorText(receivedItem, 'wastageQuantity')}
                                  InputProps={{
                                    inputProps: { min: 0 }
                                  }}
                                />
                              </TableCell>
                              {processMaterial && (
                                <>
                                  <TableCell align="right">
                                    <TextField
                                      type="number"
                                      value={receivedItem.processingRate || 0}
                                      onChange={(e) => handleItemChange(
                                        receivedItemIndex,
                                        'processingRate',
                                        Number(e.target.value) || 0
                                      )}
                                      size="small"
                                      InputProps={{
                                        inputProps: { min: 0, step: 0.01 },
                                        startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    ₹{((receivedItem.materialQuantity || 0) * (receivedItem.processingRate || 0)).toFixed(2)}
                                  </TableCell>
                                </>
                              )}
                              <TableCell>
                                <TextField
                                  value={receivedItem.remarks || ''}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'remarks',
                                    e.target.value
                                  )}
                                  size="small"
                                  fullWidth
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={processMaterial ? 7 : 5} align="center">
                            No materials found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {activeTab === 1 && !processMaterial && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Garment Part</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell align="right">Pending Receipt</TableCell>
                        <TableCell align="right">Receive Qty</TableCell>
                        <TableCell align="right">Defect Qty</TableCell>
                        <TableCell align="right">Shortage Qty</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {designDetails && designDetails.length > 0 ? (
                        designDetails.map((design, designIndex) => {
                          const receivedItem = receivedItems.find(item => 
                            item.garmentPart === design.garmentPart && 
                            item.size === design.size
                          );
                          if (!receivedItem) return null;
                          
                          const receivedItemIndex = receivedItems.findIndex(item => 
                            item.garmentPart === design.garmentPart && 
                            item.size === design.size
                          );
                          
                          return (
                            <TableRow key={designIndex}>
                              <TableCell>{design.garmentPart}</TableCell>
                              <TableCell>{design.size}</TableCell>
                              <TableCell align="right">
                                {pendingByDesign[`${design.garmentPart}|${design.size}`] ?? 0} PCS
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.pieceCount || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'pieceCount',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  error={hasItemError(receivedItem, 'pieceCount')}
                                  helperText={getItemErrorText(receivedItem, 'pieceCount')}
                                  InputProps={{
                                    inputProps: { min: 0 }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.defectQuantity || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'defectQuantity',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  error={hasItemError(receivedItem, 'defectQuantity')}
                                  helperText={getItemErrorText(receivedItem, 'defectQuantity')}
                                  InputProps={{
                                    inputProps: { min: 0 }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.shortageQuantity || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'shortageQuantity',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  error={hasItemError(receivedItem, 'shortageQuantity')}
                                  helperText={getItemErrorText(receivedItem, 'shortageQuantity')}
                                  InputProps={{
                                    inputProps: { min: 0 }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={receivedItem.processingRate || 0}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'processingRate',
                                    Number(e.target.value) || 0
                                  )}
                                  size="small"
                                  InputProps={{
                                    inputProps: { min: 0, step: 0.01 },
                                    startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                ₹{((receivedItem.pieceCount || 0) * (receivedItem.processingRate || 0)).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  value={receivedItem.remarks || ''}
                                  onChange={(e) => handleItemChange(
                                    receivedItemIndex,
                                    'remarks',
                                    e.target.value
                                  )}
                                  size="small"
                                  fullWidth
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No design details found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <ValidationErrors />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptDialog; 